import React from 'react';
import { BookingPositionState } from '../model/BookingPositionState';
import {
	compsetPlaceText,
	euroText,
	formatCount,
	middleHalfText,
	nightCountTexts,
	ordinal,
	percentText,
	positionText,
	sameCityHotels,
	scoreText,
	windowText,
} from '../BookingRanking.utilities';
import { BadgeKey, HotelNameCell, KeyList, RankingCard, RankingCardHead, TD, TH } from '../BookingRankingParts';
import { LEVEL_KEY, ORDER_KEY, priceKeys, typicalKeys } from '../CompsetTable';

// Compset details: every value of the compset hotels over all nights of the window.
const CompsetDetailsTable: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const hotels = sameCityHotels(state);
	const scan = state.scan;
	const checks = scan?.window === 'FIRST_SCAN' ? `, ${scan.checksPerNight} checks per night` : '';
	return <RankingCard>
		<RankingCardHead title="Compset details"
			subtitle={`All ${state.nights.length} nights${scan !== null ? ` · ${windowText(scan)}${checks}` : ''}`} />
		<div className="overflow-x-auto">
			<table className="min-w-full">
				<thead><tr className="border-b border-gray-200">
					<th className={`${TH} pl-4 sm:pl-5`}>#</th>
					<th className={`${TH} sticky left-0 z-10 bg-white`}>Hotel</th>
					<th className={`${TH} text-right`}>Typical</th>
					<th className={TH}>Middle half</th>
					<th className={TH}>Best–worst</th>
					<th className={TH}>Nights (of {state.nights.length})</th>
					<th className={`${TH} text-right`}>Typical price</th>
					<th className={`${TH} text-right`}>Price rank</th>
					<th className={`${TH} text-right`}>City: dearer than</th>
					<th className={`${TH} pr-4 sm:pr-5`}>Review</th>
				</tr></thead>
				<tbody className="divide-y divide-gray-100">
					{hotels.map((hotel) => {
						const nights = nightCountTexts(hotel);
						const bg = hotel.isMain ? 'bg-sky-50' : 'bg-white';
						return <tr key={hotel.hotelId} className={bg}>
							<td className={`${TD} whitespace-nowrap pl-4 text-gray-600 sm:pl-5`}>{compsetPlaceText(hotel)}</td>
							<td className={`${TD} sticky left-0 z-10 ${bg}`}><span className="block w-28 sm:w-64"><HotelNameCell hotel={hotel} /></span></td>
							<td className={`${TD} whitespace-nowrap text-right font-bold`}>{positionText(hotel.typical, hotel.typicalAfter500)}</td>
							<td className={`${TD} whitespace-nowrap`}>{middleHalfText(hotel)}</td>
							<td className={`${TD} whitespace-nowrap`}>{hotel.best === null ? '–' : `${hotel.best}–${hotel.worst}`}</td>
							<td className={TD}>
								<span className="block w-40">{nights.first}
									<span className="block text-xs text-gray-600">{nights.rest}</span>
								</span>
							</td>
							<td className={`${TD} text-right`}>{euroText(hotel.typicalPrice)}</td>
							<td className={`${TD} text-right font-semibold`}>{hotel.compsetPriceRank ?? '–'}</td>
							<td className={`${TD} whitespace-nowrap text-right`}>{percentText(hotel.cityDearerShare)}
								{hotel.cityPriceRank !== null && hotel.cityOnSaleCount !== null && <span className="block text-xs text-gray-600">
									about {ordinal(hotel.cityPriceRank)} of {formatCount(hotel.cityOnSaleCount)}
								</span>}
							</td>
							<td className={`${TD} whitespace-nowrap pr-4 sm:pr-5`}>{scoreText(hotel.reviewScore)}
								{hotel.reviewCount !== null && <span className="block text-xs text-gray-600">{formatCount(hotel.reviewCount)} reviews</span>}
							</td>
						</tr>;
					})}
				</tbody>
			</table>
		</div>
		<div className="flex flex-col gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
			<BadgeKey />
			<KeyList items={[
				...typicalKeys(),
				{ term: 'Best–worst', text: 'the best and the worst check that found the hotel.' },
				{ term: 'Not found', text: 'no check found the hotel, and RoomPulse\'s latest price check shows it sold out or RoomPulse has no price check of that night (the night is left out).' },
				...(hotels.some((h) => h.levelWithNeighbour) ? [LEVEL_KEY] : []),
				...priceKeys(state.bookingCity.name),
				{ text: 'The small line under the city share: about which place the hotel takes when all properties on sale stand in order of price (1st = the cheapest).' },
				ORDER_KEY,
			]} />
		</div>
	</RankingCard>;
};

export default CompsetDetailsTable;
